import React from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";

interface ScheduleConflictDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  existingChange: {
    change_date?: string;
    items?: any[];
  };
  proposedChange: {
    change_date?: string;
    items?: any[];
  };
  loading?: boolean;
}

export const ScheduleConflictDialog: React.FC<ScheduleConflictDialogProps> = ({
  open,
  onClose,
  onConfirm,
  existingChange,
  proposedChange,
  loading = false,
}) => {
  if (!open) return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return "end of billing period";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        />

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-amber-50 px-4 py-4 sm:px-6 flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Plan Change Already Scheduled
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-4">
              {/* Existing change info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Currently Scheduled Change
                </h4>
                <p className="text-sm text-gray-600">
                  Scheduled for:{" "}
                  <span className="font-medium">
                    {formatDate(existingChange.change_date)}
                  </span>
                </p>
                {existingChange.items && existingChange.items.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {existingChange.items.length} pricing item(s) configured
                  </p>
                )}
              </div>

              {/* Proposed change info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  New Proposed Change
                </h4>
                <p className="text-sm text-gray-600">
                  Would take effect:{" "}
                  <span className="font-medium">
                    {formatDate(proposedChange.change_date)}
                  </span>
                </p>
                {proposedChange.items && proposedChange.items.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {proposedChange.items.length} pricing item(s) configured
                  </p>
                )}
              </div>

              {/* Warning message */}
              <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Replacing the existing scheduled change
                  will cancel it and schedule your new plan change instead. This
                  action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={onConfirm}
              disabled={loading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-amber-600 text-base font-medium text-white hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Processing..." : "Replace Existing Change"}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Keep Existing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
